(() => {
  const availabilityGuardRetryDelays = [100, 500, 1000, 2000, 4000];

  const installNativeAvailabilityGuard = (root) => {
    if (!root || root.dataset.fdNativeAvailabilityGuardReady === 'true') return;
    if (root.dataset.fdNativeAvailabilityGuardInitializing === 'true') return;
    root.dataset.fdNativeAvailabilityGuardInitializing = 'true';

    try {
      let purchaseTransportBusy = false;
      let releaseTransportLabel = false;
      let updateQueued = false;
      const customPurchaseFlow = root.dataset.customizable === 'true'
        || root.dataset.windowTreatment === 'true';

      const getCurrentVariant = () => {
        const variantInput = root.querySelector('[data-fd-variant-id]');
        const variantJson = root.querySelector('[data-fd-variants-json]');
        if (!variantInput || !variantJson) return null;

        let variants = [];
        try {
          variants = JSON.parse(variantJson.textContent || '[]');
        } catch (error) {
          return null;
        }

        const variantId = String(variantInput.value || '');
        if (!variantId) return null;
        const variant = variants.find((item) => String(item.id) === variantId) || null;
        return variant && typeof variant.available === 'boolean' ? variant : null;
      };

      const queryPurchaseButtons = () => Array.from(new Set(root.querySelectorAll([
        '[data-fd-atc]',
        '[data-fd-sticky-submit]',
        '.fd-pdp-form button[name="add"]',
        '.fd-pdp-form input[name="add"]',
        '.fd-pdp-form button[type="submit"]',
        '.fd-pdp-form input[type="submit"]'
      ].join(', '))));

      const getButtonLabel = (button) => {
        if (!button) return '';
        if (button.matches('input')) return String(button.value || '').trim();
        const labelNode = button.matches(
          '[data-fd-atc], .fd-pdp-form button[name="add"], .fd-pdp-form button[type="submit"]'
        )
          ? button.querySelector('[data-fd-atc-text]')
          : button;
        return String(labelNode ? labelNode.textContent : button.textContent || '').trim();
      };

      const setButtonLabel = (button, label) => {
        if (!button || !label) return;
        if (button.matches('input')) {
          if (String(button.value || '').trim() !== label) button.value = label;
          return;
        }

        const isPrimaryButton = button.matches(
          '[data-fd-atc], .fd-pdp-form button[name="add"], .fd-pdp-form button[type="submit"]'
        );
        let labelNode = isPrimaryButton ? button.querySelector('[data-fd-atc-text]') : button;
        if (!labelNode && isPrimaryButton) {
          labelNode = document.createElement('span');
          labelNode.setAttribute('data-fd-atc-text', '');
          button.replaceChildren(labelNode);
        }
        if (labelNode && String(labelNode.textContent || '').trim() !== label) {
          labelNode.textContent = label;
        }
      };

      const isBusyButton = (button) => {
        if (purchaseTransportBusy || !button) return true;
        if (releaseTransportLabel) return false;
        const hasBusyFlag = button.getAttribute('aria-busy') === 'true'
          || button.classList.contains('is-loading')
          || button.dataset.loading === 'true';
        return hasBusyFlag && /^(?:Adding(?:\.{3}|…)?|Added)$/i.test(getButtonLabel(button));
      };

      const enforceNativeAvailability = () => {
        if (!root.isConnected) return;
        const variant = getCurrentVariant();
        const available = customPurchaseFlow || !variant || variant.available !== false;
        const label = available ? 'Add to Cart' : 'Sold Out';
        queryPurchaseButtons().forEach((button) => {
          if (!button.isConnected || isBusyButton(button)) return;
          if (button.disabled === available) button.disabled = !available;
          const ariaDisabled = available ? 'false' : 'true';
          if (button.getAttribute('aria-disabled') !== ariaDisabled) {
            button.setAttribute('aria-disabled', ariaDisabled);
          }
          setButtonLabel(button, label);
        });
        releaseTransportLabel = false;
      };

      const scheduleNativeAvailability = () => {
        if (updateQueued) return;
        updateQueued = true;
        Promise.resolve().then(() => {
          updateQueued = false;
          enforceNativeAvailability();
        });
      };

      enforceNativeAvailability();

      const observer = new MutationObserver(scheduleNativeAvailability);
      observer.observe(root, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'data-fd-atc', 'name', 'type'],
        childList: true,
        characterData: true,
        subtree: true
      });

      root.addEventListener('fd:purchase-transport', (event) => {
        purchaseTransportBusy = Boolean(event && event.detail && event.detail.busy);
        if (!purchaseTransportBusy) {
          releaseTransportLabel = true;
          scheduleNativeAvailability();
        }
      });
      root.addEventListener('fd:variant-updated', scheduleNativeAvailability);
      root.addEventListener('change', scheduleNativeAvailability, true);
      root.addEventListener('input', scheduleNativeAvailability, true);

      root.dataset.fdNativeAvailabilityGuardReady = 'true';
      delete root.dataset.fdNativeAvailabilityGuardInitializing;
      delete root.dataset.fdNativeAvailabilityGuardRetry;
      delete root.dataset.fdNativeAvailabilityGuardError;

      availabilityGuardRetryDelays.forEach((delay) => {
        window.setTimeout(scheduleNativeAvailability, delay);
      });
    } catch (error) {
      delete root.dataset.fdNativeAvailabilityGuardReady;
      delete root.dataset.fdNativeAvailabilityGuardInitializing;
      root.dataset.fdNativeAvailabilityGuardError = 'true';
      const retryAttempt = Number(root.dataset.fdNativeAvailabilityGuardRetry || '0');
      if (retryAttempt < availabilityGuardRetryDelays.length) {
        root.dataset.fdNativeAvailabilityGuardRetry = String(retryAttempt + 1);
        window.setTimeout(
          () => installNativeAvailabilityGuard(root),
          availabilityGuardRetryDelays[retryAttempt]
        );
      }
    }
  };

  const initialize = (root) => {
    if (!root) return;
    installNativeAvailabilityGuard(root);
    if (root.dataset.fdP0Ready === 'true') return;
    root.dataset.fdP0Ready = 'true';

    const form = root.querySelector('.fd-pdp-form');
    const variantInput = root.querySelector('[data-fd-variant-id]');
    const variantJson = root.querySelector('[data-fd-variants-json]');
    const isWindowTreatment = root.dataset.windowTreatment === 'true';
    let isCustomizable = isWindowTreatment || root.dataset.customizable === 'true';
    const basePriceNodes = Array.from(root.querySelectorAll('[data-fd-base-price]'));
    const primaryPriceNodes = Array.from(root.querySelectorAll('[data-fd-primary-price]'));
    const primaryPriceLabels = Array.from(root.querySelectorAll('[data-fd-primary-price-label]'));
    const primaryPriceWraps = Array.from(root.querySelectorAll('[data-fd-primary-price-wrap]'));
    const configuredPriceNodes = Array.from(root.querySelectorAll('[data-fd-price], [data-fd-sticky-price]'));
    const addOnPriceNodes = Array.from(root.querySelectorAll('[data-fd-addon-price]'));
    const lineTotalNodes = Array.from(root.querySelectorAll('[data-fd-line-total]'));
    const orderQuantityNodes = Array.from(root.querySelectorAll('[data-fd-order-quantity]'));
    const savingsNodes = Array.from(root.querySelectorAll('[data-fd-savings]'));
    const comparePriceNodes = Array.from(root.querySelectorAll('[data-fd-compare-price]'));
    const quantityInput = root.querySelector('[data-fd-quantity]');
    const purchaseButtonSelector = [
      '[data-fd-atc]',
      '[data-fd-sticky-submit]',
      '.fd-pdp-form button[name="add"]',
      '.fd-pdp-form input[name="add"]',
      '.fd-pdp-form button[type="submit"]',
      '.fd-pdp-form input[type="submit"]'
    ].join(', ');
    const queryPurchaseButtons = () => Array.from(root.querySelectorAll(purchaseButtonSelector));
    let purchaseButtons = queryPurchaseButtons();
    let purchaseButtonObserver = null;
    const currency = String(window.Shopify && window.Shopify.currency && window.Shopify.currency.active || 'USD').toUpperCase();
    let variants = [];
    let priceState = isCustomizable ? 'loading' : 'ready';
    let lastAddOnSignature = '';
    let stableAddOnReads = 0;
    let confirmationFrame = 0;
    let bcpoMissingFinal = false;
    let latestSnapshot = null;
    let selectionGeneration = 0;
    let priceMutationConfirmedGeneration = 0;
    let lastObservedSelectionSignature = '';
    let confirmationNotBefore = 0;
    let priceConfirmationDeadline = Date.now() + 8000;
    let snapshotRevision = 0;
    let lastSnapshotMaterialSignature = '';
    let purchaseTransportBusy = false;
    let pendingSelectionSignature = '';
    let lastStableSelectionSignature = '';
    let stableSelectionReads = 0;
    let selectionChangedAt = 0;
    const selectionSettleMs = 650;
    const mutationQuietMs = 650;
    const stableReadIntervalMs = 100;
    const unchangedPriceConfirmationMs = 2200;
    const priceConfirmationTimeoutMs = 8000;
    const guardedColorProducts = new Map([
      ['custom-ivy-motorized-bamboo-shades-blinds', '9267957629178'],
      ['custom-natural-motorized-bamboo-shades-blinds', '9267981189370'],
      ['custom-nomi-motorized-bamboo-shades-blinds', '9267998163194']
    ]);
    const colorPriceGuardVersion = 'negative-color-v2';

    try {
      variants = JSON.parse(variantJson ? variantJson.textContent : '[]');
    } catch (error) {
      variants = [];
    }

    const getCurrentVariant = () => {
      if (!variants.length || !variantInput) return null;
      const variantId = variantInput ? String(variantInput.value || '') : '';
      if (!variantId) return null;
      return variants.find((item) => String(item.id) === variantId) || null;
    };

    const updateBasePrice = () => {
      if (!variantInput || !basePriceNodes.length || !variants.length) return;
      const variant = getCurrentVariant();
      if (!variant) return;
      const formatted = variant.price_formatted || formatConfiguredPrice(Number(variant.price) || 0);
      basePriceNodes.forEach((node) => {
        if (node.textContent !== formatted) node.textContent = formatted;
      });
      if (!isCustomizable) {
        primaryPriceNodes.forEach((node) => {
          if (node.textContent !== formatted) node.textContent = formatted;
        });
      }
    };

    const updateSavings = () => {
      const variant = getCurrentVariant();
      const priceCents = variant && Number.isFinite(Number(variant.price)) ? Number(variant.price) : 0;
      const compareCents = variant && Number.isFinite(Number(variant.compare_at_price)) ? Number(variant.compare_at_price) : 0;
      const savingsCents = compareCents > priceCents ? compareCents - priceCents : 0;
      comparePriceNodes.forEach((node) => {
        node.hidden = !savingsCents;
        if (savingsCents) node.textContent = formatConfiguredPrice(compareCents);
      });
      savingsNodes.forEach((node) => {
        if (!savingsCents || !compareCents) {
          if (!node.hidden) node.hidden = true;
          return;
        }
        const percent = Math.round((savingsCents / compareCents) * 100);
        const label = String(node.dataset.fdSaveLabel || 'Save');
        const savingsText = `${label} ${formatConfiguredPrice(savingsCents)} (${percent}%)`;
        if (node.textContent !== savingsText) node.textContent = savingsText;
        if (node.hidden) node.hidden = false;
      });
    };

    const getBcpoAddOnReading = () => {
      const addOnNode = root.querySelector('.bcpo-addons-price') || document.querySelector('.bcpo-addons-price');
      if (!addOnNode) return { ready: false, cents: null, signature: 'missing' };
      const text = String(addOnNode.textContent || '').replace(/\u00a0/g, ' ').trim();
      const normalized = text.replace(/,/g, '');
      const currencyMatch = normalized.match(/(-?)\s*(?:USD|US\$|\$)\s*(-?\d+(?:\.\d{1,2})?)/i);
      const numericMatches = Array.from(normalized.matchAll(/-?\d+(?:\.\d{1,2})?/g));
      const amountText = currencyMatch
        ? `${currencyMatch[1] === '-' && !currencyMatch[2].startsWith('-') ? '-' : ''}${currencyMatch[2]}`
        : numericMatches.length
          ? numericMatches[numericMatches.length - 1][0]
          : '';
      if (!amountText) return { ready: false, cents: null, signature: `invalid:${text}` };
      const amount = Number(amountText);
      if (!Number.isFinite(amount)) {
        return { ready: false, cents: null, signature: `invalid:${text}` };
      }
      const cents = Math.round(amount * 100);
      return { ready: true, cents, signature: `cents:${cents}` };
    };

    const getGuardedColorReading = () => {
      const handle = String(root.dataset.productHandle || '').trim().toLowerCase();
      const expectedProductId = guardedColorProducts.get(handle);
      if (!expectedProductId) {
        return { required: false, selected: false, valid: true, cents: null };
      }
      if (String(root.dataset.productId || '').trim() !== expectedProductId) {
        return { required: true, selected: true, valid: false, cents: null };
      }

      const bcpoRoot = getBcpoRoot();
      if (!bcpoRoot) {
        return { required: true, selected: false, valid: false, cents: null };
      }

      const radio = bcpoRoot.querySelector('input[name="properties[Color]"]:checked');
      const select = bcpoRoot.querySelector('select[name="properties[Color]"]');
      const control = radio || select;
      if (!control || !String(control.value || '').trim()) {
        return { required: true, selected: false, valid: false, cents: null };
      }

      const selectedOption = control.tagName === 'SELECT'
        ? control.options[control.selectedIndex]
        : null;
      const rawPriceValue = control.getAttribute('data-price')
        ?? (selectedOption && selectedOption.getAttribute('data-price'));
      const rawPrice = String(rawPriceValue == null ? '' : rawPriceValue).trim();
      if (!/^[+-]?\d+(?:\.\d{1,2})?$/.test(rawPrice)) {
        return { required: true, selected: true, valid: false, cents: null };
      }

      const amount = Number(rawPrice);
      const cents = Math.round(amount * 100);
      return {
        required: true,
        selected: true,
        valid: Number.isFinite(amount) && Number.isSafeInteger(cents),
        cents: Number.isFinite(amount) && Number.isSafeInteger(cents) ? cents : null
      };
    };

    const formatConfiguredPrice = (cents) => {
      try {
        return `${new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(cents / 100)} ${currency}`;
      } catch (error) {
        return `$${(cents / 100).toFixed(2)} ${currency}`;
      }
    };

    const normalizeText = (value) => String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

    const getBcpoRoot = () => {
      const bcpoRoot = root.querySelector('#bcpo') || (
        document.querySelectorAll('[data-fd-product]').length === 1 ? document.querySelector('#bcpo') : null
      );
      if (bcpoRoot && bcpoRoot.querySelector('input, select, textarea, [data-option], .selector-wrapper, .bold_option')) {
        isCustomizable = true;
        root.dataset.customizable = 'true';
      }
      return bcpoRoot;
    };

    const getWrapperTitle = (wrapper) => {
      const heading = wrapper && wrapper.querySelector
        ? wrapper.querySelector('.bcpo-title, .bcpo-label, .bold_option_title, legend, label')
        : null;
      return String(
        wrapper && (wrapper.dataset.optionTitle || wrapper.dataset.option || wrapper.dataset.bcpoOption)
        || heading && heading.textContent
        || ''
      ).replace(/\s*\*+\s*$/, '').trim();
    };

    const getActiveRequiredWrappers = () => {
      const bcpoRoot = getBcpoRoot();
      if (!bcpoRoot) return [];
      const wrappers = Array.from(bcpoRoot.querySelectorAll(
        '.selector-wrapper, .bcpo-selector-wrapper, .bold_option, [data-option]'
      ));
      return wrappers.filter((wrapper) => {
        const controls = Array.from(wrapper.querySelectorAll('input, select, textarea'))
          .filter((control) => (
            !control.disabled
            && control.type !== 'hidden'
            && !String(control.name || '').includes('[_fd_')
          ));
        if (!controls.length) return false;
        const titleText = String(
          (wrapper.querySelector('.bcpo-title, .bcpo-label, .bold_option_title, legend') || {}).textContent || ''
        );
        const required = controls.some((control) => control.required || control.getAttribute('aria-required') === 'true')
          || wrapper.getAttribute('aria-required') === 'true'
          || /\brequired\b/i.test(wrapper.className || '')
          || /\*/.test(titleText);
        if (!required) return false;

        const mappedHidden = wrapper.classList.contains('fd-bcpo-template-hidden')
          && wrapper.dataset.fdVoSyncConfirmed === 'true';
        const conditionallyInactive = !mappedHidden && (
          wrapper.hidden
          || wrapper.getAttribute('aria-hidden') === 'true'
          || window.getComputedStyle(wrapper).display === 'none'
        );
        return !conditionallyInactive;
      });
    };

    const wrapperHasValue = (wrapper) => {
      const controls = Array.from(wrapper.querySelectorAll('input, select, textarea'))
        .filter((control) => (
          !control.disabled
          && control.type !== 'hidden'
          && !String(control.name || '').includes('[_fd_')
        ));
      if (!controls.length) return false;
      const radios = controls.filter((control) => control.type === 'radio');
      if (radios.length) return radios.some((control) => control.checked && String(control.value || '').trim() !== '');
      const checkboxes = controls.filter((control) => control.type === 'checkbox');
      if (checkboxes.length) return checkboxes.some((control) => control.checked);
      return controls.some((control) => {
        const value = String(control.value == null ? '' : control.value).trim();
        if (!value) return false;
        if (control.tagName === 'SELECT') {
          const selected = control.options && control.options[control.selectedIndex];
          if (selected && (selected.disabled || selected.dataset.placeholder === 'true')) return false;
        }
        return true;
      });
    };

    const getIncompleteRequiredTitles = () => getActiveRequiredWrappers()
      .filter((wrapper) => !wrapperHasValue(wrapper))
      .map(getWrapperTitle)
      .filter(Boolean);

    const canonicalPropertyValue = (value) => String(value == null ? '' : value)
      .replace(/\s*\|\s*-?\s*(?:(?:US|CA|C)?\$|USD|CAD|EUR|GBP)\s*-?\s*[0-9,]+(?:\.[0-9]{1,2})?\s*$/i, '')
      .trim();

    const propertyNameFromControl = (name) => {
      const match = String(name || '').trim().match(/^properties\[(.+)\]$/);
      return match ? match[1].trim() : '';
    };

    const getOriginalSelectionPairs = () => {
      const bcpoRoot = getBcpoRoot();
      const controls = form
        ? Array.from(form.elements || [])
        : bcpoRoot
          ? Array.from(bcpoRoot.querySelectorAll('input, select, textarea'))
          : [];
      const pairs = [];
      controls.forEach((control) => {
        if (control.disabled) return;
        const name = String(control.name || '').trim();
        if (!name || !name.includes('properties[') || name.includes('properties[_fd_')) return;
        if ((control.type === 'radio' || control.type === 'checkbox') && !control.checked) return;
        const propertyName = propertyNameFromControl(name);
        const value = canonicalPropertyValue(control.value);
        if (!propertyName || !value) return;
        pairs.push(`${propertyName}=${value}`);
      });
      return Array.from(new Set(pairs)).sort();
    };

    const getObservedConfigurationSignature = (selectionPairs = getOriginalSelectionPairs()) => [
      `variant=${variantInput ? String(variantInput.value || '') : ''}`,
      ...selectionPairs
    ].join('\n');

    const selectionChecksum = (pairs) => {
      const text = pairs.join('\n');
      let hash = 2166136261;
      for (let index = 0; index < text.length; index += 1) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
      }
      return `fnv1a-${(hash >>> 0).toString(16).padStart(8, '0')}`;
    };

    const getOptionSchemaHash = () => {
      const bcpoRoot = getBcpoRoot();
      if (!bcpoRoot) return '';
      const schemaPairs = Array.from(bcpoRoot.querySelectorAll(
        '.selector-wrapper, .bcpo-selector-wrapper, .bold_option, [data-option]'
      )).map((wrapper) => {
        const controls = Array.from(wrapper.querySelectorAll('input, select, textarea'))
          .filter((control) => control.type !== 'hidden');
        const identifiers = controls.map((control) => [
          String(control.name || ''),
          String(control.type || control.tagName || ''),
          control.required || control.getAttribute('aria-required') === 'true' ? 'required' : 'optional'
        ].join(':')).sort();
        return `${getWrapperTitle(wrapper)}|${identifiers.join(',')}`;
      }).filter(Boolean).sort();
      return selectionChecksum(schemaPairs);
    };

    const privatePropertyNames = {
      customization: '_fd_customization',
      base: '_fd_base_price_cents',
      addon: '_fd_addon_cents',
      configured: '_fd_configured_unit_cents',
      currency: '_fd_currency',
      checksum: '_fd_selection_checksum',
      state: '_fd_price_state',
      guardVersion: '_fd_price_guard_version',
      colorAdjustment: '_fd_color_adjustment_cents'
    };

    const setPrivateProperty = (key, value) => {
      if (!form) return;
      const propertyName = privatePropertyNames[key];
      let input = form.querySelector(`input[data-fd-private-price="${propertyName}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${propertyName}]`;
        input.setAttribute('data-fd-private-price', propertyName);
        form.appendChild(input);
      }
      input.value = String(value == null ? '' : value);
    };

    const syncPrivateProperties = (snapshot) => {
      if (!isCustomizable || !snapshot) return;
      setPrivateProperty('customization', '1');
      setPrivateProperty('base', snapshot.baseCents);
      setPrivateProperty('addon', snapshot.addOnCents == null ? '' : snapshot.addOnCents);
      setPrivateProperty('configured', snapshot.configuredUnitCents == null ? '' : snapshot.configuredUnitCents);
      setPrivateProperty('currency', currency);
      setPrivateProperty('checksum', snapshot.selectionChecksum || '');
      setPrivateProperty('state', snapshot.state);
      if (snapshot.colorGuardRequired) {
        const guardReady = snapshot.state === 'ready'
          && snapshot.colorGuardValid
          && Number.isSafeInteger(snapshot.colorAdjustmentCents);
        setPrivateProperty('guardVersion', guardReady ? colorPriceGuardVersion : '');
        setPrivateProperty('colorAdjustment', guardReady ? snapshot.colorAdjustmentCents : '');
      }
    };

    const getFormError = () => {
      if (!form) return null;
      let error = form.querySelector('[data-fd-pdp-form-error]');
      if (!error) {
        error = document.createElement('p');
        error.setAttribute('data-fd-pdp-form-error', '');
        error.setAttribute('role', 'alert');
        error.style.color = '#8b2e24';
        error.style.fontSize = '13px';
        error.style.lineHeight = '1.45';
        error.style.margin = '8px 0';
        const submit = form.querySelector('[data-fd-atc]');
        form.insertBefore(error, submit || null);
      }
      return error;
    };

    const showFormError = (message) => {
      const error = getFormError();
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    };

    const clearFormError = () => {
      const error = form && form.querySelector('[data-fd-pdp-form-error]');
      if (error) error.hidden = true;
    };

    const setPriceText = (nodes, text) => nodes.forEach((node) => {
      if (node.textContent !== text) node.textContent = text;
    });

    const setPriceState = (state, baseCents, addOnCents, incompleteTitles = []) => {
      priceState = state;
      const quantity = Math.max(Number.parseInt(quantityInput ? quantityInput.value : '1', 10) || 1, 1);
      const configuredUnitCents = state === 'ready' ? baseCents + (addOnCents || 0) : null;
      const colorGuard = getGuardedColorReading();
      const selectionPairs = getOriginalSelectionPairs();
      const digest = selectionChecksum(selectionPairs);
      const variantId = variantInput ? String(variantInput.value || '') : '';
      const optionSchemaHash = getOptionSchemaHash();
      const materialSignature = JSON.stringify([
        state,
        variantId,
        baseCents,
        state === 'ready' ? (addOnCents || 0) : null,
        configuredUnitCents,
        quantity,
        currency,
        optionSchemaHash,
        digest,
        incompleteTitles,
        colorGuard.required,
        colorGuard.selected,
        colorGuard.valid,
        colorGuard.cents
      ]);
      lastObservedSelectionSignature = getObservedConfigurationSignature(selectionPairs);
      if (materialSignature === lastSnapshotMaterialSignature && latestSnapshot) {
        enforcePurchaseButtonState();
        return latestSnapshot;
      }
      lastSnapshotMaterialSignature = materialSignature;
      snapshotRevision += 1;
      latestSnapshot = {
        version: 1,
        state,
        variantId,
        basePriceCents: baseCents,
        addonCents: state === 'ready' ? (addOnCents || 0) : null,
        baseCents,
        addOnCents: state === 'ready' ? (addOnCents || 0) : null,
        configuredUnitCents,
        quantity,
        lineTotalCents: configuredUnitCents == null ? null : configuredUnitCents * quantity,
        currency,
        optionSchemaHash,
        selectionDigest: digest,
        selectionChecksum: digest,
        revision: snapshotRevision,
        serverQuoteStatus: 'unavailable',
        serverQuoteId: null,
        serverQuoteExpiresAt: null,
        selections: selectionPairs,
        incompleteTitles,
        colorGuardRequired: colorGuard.required,
        colorGuardSelected: colorGuard.selected,
        colorGuardValid: colorGuard.valid,
        colorAdjustmentCents: colorGuard.cents,
        priceGuardVersion: colorGuard.required ? colorPriceGuardVersion : ''
      };

      root.dataset.fdPriceState = state;
      root.dataset.fdBasePriceCents = String(baseCents);
      root.dataset.fdAddOnCents = addOnCents == null ? '' : String(addOnCents);
      root.dataset.fdConfiguredUnitCents = configuredUnitCents == null ? '' : String(configuredUnitCents);
      root.dataset.fdPriceGuardVersion = colorGuard.required ? colorPriceGuardVersion : '';
      root.dataset.fdColorAdjustmentCents = Number.isSafeInteger(colorGuard.cents)
        ? String(colorGuard.cents)
        : '';
      const primaryLabel = !isCustomizable
        ? 'Price'
        : state === 'ready'
          ? 'Configured total'
          : state === 'error'
            ? 'Price unavailable'
            : 'Starting at';
      setPriceText(primaryPriceLabels, primaryLabel);
      primaryPriceLabels.forEach((node) => {
        node.hidden = !isCustomizable;
      });
      primaryPriceWraps.forEach((node) => {
        node.setAttribute('aria-label', primaryLabel);
      });

      const baseFormatted = formatConfiguredPrice(baseCents);
      const addOnFormatted = formatConfiguredPrice(addOnCents || 0);
      const configuredFormatted = configuredUnitCents == null ? baseFormatted : formatConfiguredPrice(configuredUnitCents);
      const lineFormatted = configuredUnitCents == null ? baseFormatted : formatConfiguredPrice(configuredUnitCents * quantity);

      setPriceText(primaryPriceNodes, state === 'error' ? '—' : state === 'loading' ? 'Calculating…' : configuredFormatted);
      setPriceText(configuredPriceNodes, state === 'error' ? 'Unavailable' : state === 'loading' ? 'Calculating…' : configuredFormatted);
      setPriceText(addOnPriceNodes, state === 'ready' ? addOnFormatted : '—');
      setPriceText(lineTotalNodes, state === 'error' ? 'Unavailable' : state === 'loading' ? 'Calculating…' : lineFormatted);
      setPriceText(orderQuantityNodes, String(quantity));

      enforcePurchaseButtonState();

      if (state === 'ready' && (addOnCents || 0) > 0) {
        comparePriceNodes.forEach((node) => { node.hidden = true; });
        savingsNodes.forEach((node) => { node.hidden = true; });
      } else {
        updateSavings();
      }

      if (state !== 'error') clearFormError();
      syncPrivateProperties(latestSnapshot);
      window.FDConfiguredPrice = latestSnapshot;
      root.dispatchEvent(new CustomEvent('fd:configured-price', {
        bubbles: true,
        detail: latestSnapshot
      }));
    };

    const observeMutationTarget = (observer, target, options) => {
      if (!observer || !target || target.nodeType !== 1) return false;
      try {
        observer.observe(target, options);
        return true;
      } catch (error) {
        return false;
      }
    };

    const setPurchaseButtonLabel = (button, label) => {
      if (!button || !label || purchaseTransportBusy) return;
      if (button.matches('input')) {
        if (String(button.value || '').trim() !== label) button.value = label;
        return;
      }
      const isPrimaryPurchaseButton = button.matches(
        '[data-fd-atc], .fd-pdp-form button[name="add"], .fd-pdp-form button[type="submit"]'
      );
      let labelNode = isPrimaryPurchaseButton
        ? button.querySelector('[data-fd-atc-text]')
        : button;
      if (!labelNode && isPrimaryPurchaseButton) {
        labelNode = document.createElement('span');
        labelNode.setAttribute('data-fd-atc-text', '');
        button.replaceChildren(labelNode);
      }
      if (!labelNode) return;
      const currentLabel = String(labelNode.textContent || '').trim();
      if (currentLabel !== label) labelNode.textContent = label;
    };

    const isPurchaseButtonBusy = (button) => {
      if (purchaseTransportBusy || !button) return true;
      const hasBusyFlag = button.getAttribute('aria-busy') === 'true'
        || button.classList.contains('is-loading')
        || button.dataset.loading === 'true';
      const currentLabel = button.matches('input')
        ? String(button.value || '').trim()
        : String(button.textContent || '').trim();
      return hasBusyFlag && /^(?:Adding(?:\.{3}|…)?|Added)$/i.test(currentLabel);
    };

    const bindPurchaseButtonObservers = () => {
      const nextButtons = queryPurchaseButtons();
      const buttonsChanged = nextButtons.length !== purchaseButtons.length
        || nextButtons.some((button, index) => button !== purchaseButtons[index]);
      if (!buttonsChanged && purchaseButtonObserver) return;

      if (purchaseButtonObserver) purchaseButtonObserver.disconnect();
      purchaseButtons = nextButtons;
      if (!purchaseButtons.length) {
        purchaseButtonObserver = null;
        return;
      }

      purchaseButtonObserver = new MutationObserver((records) => {
        if (records.some((record) => (
          (record.type === 'attributes'
            && (record.attributeName === 'disabled' || record.attributeName === 'aria-disabled'))
          || record.type === 'childList'
          || record.type === 'characterData'
        ))) {
          enforcePurchaseButtonState();
        }
      });
      purchaseButtons.forEach((button) => {
        observeMutationTarget(purchaseButtonObserver, button, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          characterData: true,
          subtree: true
        });
      });
    };

    const enforcePurchaseButtonState = () => {
      bindPurchaseButtonObservers();
      const variant = getCurrentVariant();
      const available = isCustomizable || !variant || variant.available !== false;
      const shouldDisable = !available;
      const buttonLabel = available ? 'Add to Cart' : 'Sold Out';
      purchaseButtons.forEach((button) => {
        if (isPurchaseButtonBusy(button)) return;
        if (shouldDisable) {
          if (!button.disabled) button.disabled = true;
          if (button.dataset.fdPriceLock === 'true') delete button.dataset.fdPriceLock;
          if (button.getAttribute('aria-disabled') !== 'true') {
            button.setAttribute('aria-disabled', 'true');
          }
          setPurchaseButtonLabel(button, buttonLabel);
          return;
        }
        if (button.dataset.fdPriceLock === 'true') delete button.dataset.fdPriceLock;
        if (button.disabled) button.disabled = false;
        if (button.getAttribute('aria-disabled') !== 'false') {
          button.setAttribute('aria-disabled', 'false');
        }
        setPurchaseButtonLabel(button, buttonLabel);
      });
    };

    const requestStableConfirmation = (delay = stableReadIntervalMs) => {
      if (confirmationFrame) return;
      const requestedGeneration = selectionGeneration;
      const confirm = () => {
        confirmationFrame = 0;
        if (requestedGeneration !== selectionGeneration) return;
        updateConfiguredPrice();
      };
      confirmationFrame = window.setTimeout(confirm, Math.max(Number(delay) || 0, 20));
    };

    const markSelectionChange = () => {
      const now = Date.now();
      selectionGeneration += 1;
      pendingSelectionSignature = getObservedConfigurationSignature();
      lastStableSelectionSignature = '';
      stableSelectionReads = 0;
      selectionChangedAt = now;
      confirmationNotBefore = now + selectionSettleMs;
      priceConfirmationDeadline = now + priceConfirmationTimeoutMs;
    };

    const acknowledgeAddOnMutation = (confirmsCurrentSelection = false) => {
      if (confirmsCurrentSelection) {
        priceMutationConfirmedGeneration = Math.max(
          priceMutationConfirmedGeneration,
          selectionGeneration
        );
      }
      confirmationNotBefore = Math.max(confirmationNotBefore, Date.now() + mutationQuietMs);
    };

    const updateConfiguredPrice = () => {
      const bcpoRoot = getBcpoRoot();
      const variant = getCurrentVariant();
      const baseCents = variant && Number.isSafeInteger(Number(variant.price)) && Number(variant.price) >= 0
        ? Number(variant.price)
        : null;
      if (baseCents === null) {
        if (isCustomizable) {
          setPriceState('error', 0, null);
          showFormError('Customization is temporarily unavailable for this item. Please contact us for assistance.');
        }
        return;
      }
      if (!isCustomizable) {
        setPriceState('ready', baseCents, 0);
        return;
      }

      if (!bcpoRoot) {
        setPriceState(bcpoMissingFinal ? 'error' : 'loading', baseCents, null);
        if (bcpoMissingFinal) {
          showFormError('Customization is temporarily unavailable for this item. Please contact us for assistance.');
        }
        return;
      }

      const colorGuard = getGuardedColorReading();
      if (colorGuard.required && !colorGuard.selected) {
        setPriceState('incomplete', baseCents, null, ['Color']);
        return;
      }
      if (
        colorGuard.required
        && (!colorGuard.valid || !Number.isSafeInteger(colorGuard.cents))
      ) {
        setPriceState('error', baseCents, null);
        showFormError('Customization is temporarily unavailable for this item. Please contact us for assistance.');
        return;
      }
      const incompleteTitles = getIncompleteRequiredTitles();
      const mappingBlocked = root.dataset.fdVoMappingError === 'true';
      if (incompleteTitles.length || mappingBlocked) {
        setPriceState('incomplete', baseCents, null, incompleteTitles);
        return;
      }

      const addOnReading = getBcpoAddOnReading();
      if (!addOnReading.ready) {
        if (Date.now() >= priceConfirmationDeadline) {
          setPriceState('error', baseCents, null);
          showFormError('Customization is temporarily unavailable for this item. Please contact us for assistance.');
          return;
        }
        setPriceState('loading', baseCents, null);
        requestStableConfirmation(Math.max(priceConfirmationDeadline - Date.now(), 20));
        return;
      }

      if (priceMutationConfirmedGeneration < selectionGeneration) {
        const now = Date.now();
        const currentSelectionSignature = getObservedConfigurationSignature();
        if (currentSelectionSignature !== pendingSelectionSignature) {
          pendingSelectionSignature = currentSelectionSignature;
          lastObservedSelectionSignature = currentSelectionSignature;
          lastStableSelectionSignature = '';
          stableSelectionReads = 0;
          selectionChangedAt = now;
          confirmationNotBefore = Math.max(confirmationNotBefore, now + selectionSettleMs);
        }
        if (currentSelectionSignature === lastStableSelectionSignature) {
          stableSelectionReads += 1;
        } else {
          lastStableSelectionSignature = currentSelectionSignature;
          stableSelectionReads = 1;
        }

        const unchangedPriceReadyAt = Math.max(
          confirmationNotBefore,
          selectionChangedAt + unchangedPriceConfirmationMs
        );
        const stableReadbackConfirmed = Boolean(currentSelectionSignature)
          && currentSelectionSignature === pendingSelectionSignature
          && stableSelectionReads >= 2
          && now >= unchangedPriceReadyAt;

        if (stableReadbackConfirmed) {
          // A BCPO selection may legitimately keep the same add-on amount.
          // Confirm that path from stable control readback plus two stable
          // add-on reads instead of requiring a price-text mutation.
          priceMutationConfirmedGeneration = selectionGeneration;
        } else if (now >= priceConfirmationDeadline) {
          setPriceState('error', baseCents, null);
          showFormError('Customization is temporarily unavailable for this item. Please contact us for assistance.');
          return;
        } else {
          setPriceState('loading', baseCents, null);
          const nextReadDelay = now < unchangedPriceReadyAt
            ? Math.min(Math.max(unchangedPriceReadyAt - now, stableReadIntervalMs), 600)
            : stableReadIntervalMs;
          requestStableConfirmation(nextReadDelay);
          return;
        }
      }

      const remainingSettleMs = confirmationNotBefore - Date.now();
      if (remainingSettleMs > 0) {
        setPriceState('loading', baseCents, null);
        requestStableConfirmation(remainingSettleMs);
        return;
      }

      if (addOnReading.signature === lastAddOnSignature) stableAddOnReads += 1;
      else {
        lastAddOnSignature = addOnReading.signature;
        stableAddOnReads = 1;
      }
      if (stableAddOnReads < 2) {
        setPriceState('loading', baseCents, null);
        requestStableConfirmation();
        return;
      }
      if (baseCents + addOnReading.cents < 0) {
        setPriceState('error', baseCents, null);
        showFormError('Customization is temporarily unavailable for this item. Please contact us for assistance.');
        return;
      }
      setPriceState('ready', baseCents, addOnReading.cents);
    };

    const enforceForceHiddenBcpoFields = () => {
      root.querySelectorAll('#bcpo .fd-bcpo-force-hidden').forEach((wrapper) => {
        if (wrapper.dataset.fdVoSyncConfirmed !== 'true') {
          wrapper.classList.remove('fd-bcpo-force-hidden');
          wrapper.hidden = false;
          wrapper.removeAttribute('aria-hidden');
          wrapper.style.removeProperty('display');
          return;
        }
        if (!wrapper.hidden) wrapper.hidden = true;
        if (wrapper.getAttribute('aria-hidden') !== 'true') wrapper.setAttribute('aria-hidden', 'true');
        if (wrapper.style.getPropertyValue('display') !== 'none' || wrapper.style.getPropertyPriority('display') !== 'important') {
          wrapper.style.setProperty('display', 'none', 'important');
        }
      });
    };

    const enforceConfigurationOrder = () => {
      const ordered = [
        ['#bcpo', 10],
        ['.fd-pdp-custom', 20],
        ['.fd-pdp-included-support', 30],
        ['.fd-pdp-qty-row', 40],
        ['.fd-pdp-order-total', 50],
        ['[data-fd-pdp-form-error]', 55],
        ['.fd-pdp-atc', 60],
        ['.fd-pdp-cta-support', 70],
        ['.fd-pdp-policy-preview', 80],
        ['.fd-pdp-payments', 90]
      ];
      ordered.forEach(([selector, order]) => {
        const node = form && Array.from(form.children).find((child) => child.matches && child.matches(selector));
        if (!node) return;
        const value = String(order);
        if (node.style.getPropertyValue('order') !== value || node.style.getPropertyPriority('order') !== 'important') {
          node.style.setProperty('order', value, 'important');
        }
      });
    };

    const compactColorOptions = () => {
      const isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
      const visibleLimit = isMobile ? 9 : 12;
      root.querySelectorAll('#bcpo .fd-bcpo-color-option, [data-fd-template-color-field]').forEach((wrapper) => {
        const choices = Array.from(wrapper.querySelectorAll('.fd-bcpo-color-choice, [data-fd-template-bcpo-color]'));
        let button = wrapper.querySelector('[data-fd-p0-color-expand]');

        if (choices.length <= visibleLimit) {
          choices.forEach((choice) => {
            choice.removeAttribute('data-fd-p0-color-collapsed');
            choice.style.removeProperty('display');
          });
          if (button) button.remove();
          return;
        }

        const expanded = wrapper.dataset.fdP0ColorsExpanded === 'true';
        choices.forEach((choice, index) => {
          const selected = choice.classList.contains('fd-bcpo-is-selected')
            || choice.classList.contains('is-selected')
            || choice.getAttribute('aria-pressed') === 'true'
            || Boolean(choice.querySelector('input:checked'));
          const collapsed = !expanded && index >= visibleLimit && !selected;
          choice.setAttribute('data-fd-p0-color-collapsed', collapsed ? 'true' : 'false');
          if (collapsed) choice.style.setProperty('display', 'none', 'important');
          else choice.style.removeProperty('display');
        });

        if (!button) {
          button = document.createElement('button');
          button.type = 'button';
          button.className = 'fd-p0-color-expand';
          button.setAttribute('data-fd-p0-color-expand', '');
          wrapper.appendChild(button);
        }
        const buttonLabel = expanded ? 'Show fewer colors' : `Show all ${choices.length} colors`;
        if (button.textContent !== buttonLabel) button.textContent = buttonLabel;
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    };

    const normalizeCustomerFacingTypos = () => {
      root.querySelectorAll('[data-fd-vo-template] option').forEach((option) => {
        if (option.textContent && option.textContent.includes('Whtie')) {
          option.textContent = option.textContent.replace(/Whtie/g, 'White');
        }
      });
    };

    const installMobileChatPolish = () => {
      if (!root.classList.contains('fd-pdp--roman-ad')) return true;
      const chatAgent = document.querySelector('shopify-agent');
      const shadowRoot = chatAgent && chatAgent.shadowRoot;
      if (!chatAgent || !shadowRoot) return false;

      if (!shadowRoot.querySelector('[data-fd-pdp-chat-polish]')) {
        const style = document.createElement('style');
        style.setAttribute('data-fd-pdp-chat-polish', '');
        style.textContent = `
          @media (max-width: 749px) {
            :host([data-fd-pdp-chat-ready="false"]) .activator-nudge {
              visibility: hidden !important;
              opacity: 0 !important;
              pointer-events: none !important;
              transform: translateY(14px) !important;
            }

            .activator-nudge {
              right: 8px !important;
              bottom: 8px !important;
              width: 48px !important;
              min-width: 48px !important;
              max-width: 48px !important;
              transition: opacity .18s ease, transform .18s ease, visibility .18s ease !important;
            }

            .activator {
              width: 48px !important;
              min-width: 48px !important;
              max-width: 48px !important;
              height: 48px !important;
              min-height: 48px !important;
              max-height: 48px !important;
              padding: 0 !important;
              border-radius: 50% !important;
              grid-template-columns: 1fr !important;
              gap: 0 !important;
              justify-items: center !important;
              overflow: hidden !important;
            }

            .activator__label,
            .activator__indicator {
              display: none !important;
            }
          }
        `;
        shadowRoot.appendChild(style);
      }

      const syncChatVisibility = () => {
        const isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
        const isPastFirstView = window.scrollY > 180;
        chatAgent.setAttribute('data-fd-pdp-chat-ready', !isMobile || isPastFirstView ? 'true' : 'false');
      };

      if (chatAgent.getAttribute('data-fd-pdp-chat-bound') !== 'true') {
        chatAgent.setAttribute('data-fd-pdp-chat-bound', 'true');
        window.addEventListener('scroll', syncChatVisibility, { passive: true });
        window.addEventListener('resize', syncChatVisibility, { passive: true });
      }
      syncChatVisibility();
      return true;
    };

    const flushPrice = () => {
      updateBasePrice();
      updateSavings();
      updateConfiguredPrice();
    };

    let priceFrame = 0;
    const scheduleUpdate = (resetStability = false) => {
      if (resetStability) {
        lastAddOnSignature = '';
        stableAddOnReads = 0;
        if (confirmationFrame) {
          window.clearTimeout(confirmationFrame);
          confirmationFrame = 0;
        }
      }
      if (priceFrame) return;
      const flush = () => {
        priceFrame = 0;
        flushPrice();
      };
      priceFrame = window.setTimeout(flush, 0);
    };

    let observedAddOnNode = null;
    let addOnObserver = null;
    let lastObservedAddOnMutationSignature = '';
    const markObservedAddOnPriceMissing = () => {
      if (addOnObserver) addOnObserver.disconnect();
      addOnObserver = null;
      observedAddOnNode = null;
      if (lastObservedAddOnMutationSignature === 'missing') {
        scheduleUpdate(false);
        return;
      }
      lastObservedAddOnMutationSignature = 'missing';
      acknowledgeAddOnMutation();
      scheduleUpdate(true);
    };
    const scheduleObservedAddOnPriceUpdate = (confirmsCurrentSelection = false) => {
      const reading = getBcpoAddOnReading();
      const priceMateriallyChanged = reading.signature !== lastObservedAddOnMutationSignature;
      const pendingSelectionNeedsConfirmation = confirmsCurrentSelection
        && priceMutationConfirmedGeneration < selectionGeneration;
      if (!priceMateriallyChanged && !pendingSelectionNeedsConfirmation) return false;
      lastObservedAddOnMutationSignature = reading.signature;
      acknowledgeAddOnMutation(pendingSelectionNeedsConfirmation);
      scheduleUpdate(true);
      return true;
    };
    const observeBcpoAddOnPrice = () => {
      const addOnNode = root.querySelector('.bcpo-addons-price') || document.querySelector('.bcpo-addons-price');
      if (!addOnNode) {
        markObservedAddOnPriceMissing();
        return;
      }
      if (addOnNode === observedAddOnNode) return;
      if (addOnObserver) addOnObserver.disconnect();
      observedAddOnNode = addOnNode;
      addOnObserver = new MutationObserver((records) => {
        if (records.length && !scheduleObservedAddOnPriceUpdate(true)) scheduleUpdate(false);
      });
      observeMutationTarget(addOnObserver, addOnNode, { childList: true, subtree: true, characterData: true });
      if (!scheduleObservedAddOnPriceUpdate(true)) scheduleUpdate(false);
    };

    const runPolicyCleanup = () => {
      enforceForceHiddenBcpoFields();
      enforceConfigurationOrder();
      compactColorOptions();
      normalizeCustomerFacingTypos();
    };

    let cleanupFrame = 0;
    const schedulePolicyCleanup = () => {
      if (cleanupFrame) return;
      const cleanup = () => {
        cleanupFrame = 0;
        runPolicyCleanup();
      };
      if (window.requestAnimationFrame) {
        cleanupFrame = window.requestAnimationFrame(cleanup);
      } else {
        cleanupFrame = window.setTimeout(cleanup, 0);
      }
    };

    const nodeMatchesOrContains = (node, selector) => {
      const element = node && node.nodeType === 1 ? node : node && node.parentElement;
      if (!element) return false;
      return Boolean(
        (element.matches && element.matches(selector))
        || (element.closest && element.closest(selector))
        || (element.querySelector && element.querySelector(selector))
      );
    };

    const mutationNodesMatch = (record, selector) => {
      if (!record || record.type !== 'childList') return false;
      return Array.from(record.addedNodes || [])
        .concat(Array.from(record.removedNodes || []))
        .some((node) => nodeMatchesOrContains(node, selector));
    };

    const mutationMountsOrRemoves = (record, selector) => {
      if (!record || record.type !== 'childList') return false;
      return Array.from(record.addedNodes || [])
        .concat(Array.from(record.removedNodes || []))
        .some((node) => {
          const element = node && node.nodeType === 1 ? node : null;
          if (!element) return false;
          return Boolean(
            (element.matches && element.matches(selector))
            || (element.querySelector && element.querySelector(selector))
          );
        });
    };

    const forceHiddenNeedsRepair = (record) => {
      if (!record || record.type !== 'attributes') return false;
      const node = record.target && record.target.closest
        ? record.target.closest('#bcpo .fd-bcpo-force-hidden')
        : null;
      if (!node) return false;
      return !node.hidden
        || node.getAttribute('aria-hidden') !== 'true'
        || node.style.getPropertyValue('display') !== 'none'
        || node.style.getPropertyPriority('display') !== 'important';
    };

    let observedBcpoRoot = null;
    let bcpoObserver = null;
    const bindBcpoObserver = () => {
      const nextBcpoRoot = root.querySelector('#bcpo');
      if (nextBcpoRoot === observedBcpoRoot) {
        observeBcpoAddOnPrice();
        return Boolean(nextBcpoRoot);
      }

      if (bcpoObserver) bcpoObserver.disconnect();
      observedBcpoRoot = nextBcpoRoot || null;
      if (!observedBcpoRoot) return false;

      bcpoObserver = new MutationObserver((records) => {
        const structuralSelector = [
          '.selector-wrapper',
          '.bcpo-selector-wrapper',
          '.bold_option',
          '[data-option]',
          '.fd-bcpo-color-option',
          '[data-fd-template-color-field]',
          '.fd-bcpo-force-hidden',
          '[data-fd-vo-template]'
        ].join(', ');
        const structureChanged = records.some((record) => mutationNodesMatch(record, structuralSelector));
        const optionStateChanged = records.some((record) => (
          record.type === 'attributes'
          && ['class', 'style', 'hidden', 'aria-hidden', 'disabled', 'required', 'aria-required'].includes(record.attributeName)
          && nodeMatchesOrContains(record.target, structuralSelector)
        ));
        const selectedColorChanged = records.some((record) => (
          record.type === 'attributes'
          && record.attributeName === 'class'
          && nodeMatchesOrContains(record.target, '.fd-bcpo-color-choice, [data-fd-template-bcpo-color]')
        ));
        const addOnPriceChanged = records.some((record) => (
          (record.type === 'characterData' || record.type === 'childList')
          && (
            nodeMatchesOrContains(record.target, '.bcpo-addons-price')
            || mutationNodesMatch(record, '.bcpo-addons-price')
          )
        ));

        if (structureChanged || selectedColorChanged || records.some(forceHiddenNeedsRepair)) {
          schedulePolicyCleanup();
        }
        if (structureChanged || optionStateChanged) {
          observeBcpoAddOnPrice();
          scheduleUpdate(false);
        }
        if (addOnPriceChanged) {
          observeBcpoAddOnPrice();
          if (!scheduleObservedAddOnPriceUpdate(true)) scheduleUpdate(false);
        }
      });
      observeMutationTarget(bcpoObserver, observedBcpoRoot, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'disabled', 'required', 'aria-required']
      });
      observeBcpoAddOnPrice();
      schedulePolicyCleanup();
      scheduleUpdate();
      return true;
    };

    let structureObserver = null;
    if (root) {
      structureObserver = new MutationObserver((records) => {
        const bcpoChanged = records.some((record) => mutationMountsOrRemoves(record, '#bcpo'));
        const purchaseControlsChanged = records.some((record) => mutationMountsOrRemoves(
          record,
          purchaseButtonSelector
        )) || records.some((record) => (
          record.type === 'attributes'
          && ['data-fd-atc', 'name', 'type'].includes(record.attributeName)
          && record.target
          && record.target.closest
          && Boolean(record.target.closest('.fd-pdp-form, [data-fd-sticky-atc]'))
        ));
        const mappingStateChanged = records.some((record) => (
          record.type === 'attributes'
          && record.target === root
          && record.attributeName === 'data-fd-vo-mapping-error'
        ));
        if (bcpoChanged) bindBcpoObserver();
        if (purchaseControlsChanged) {
          bindPurchaseButtonObservers();
          enforcePurchaseButtonState();
        }
        if (bcpoChanged || purchaseControlsChanged || mappingStateChanged) {
          schedulePolicyCleanup();
          scheduleUpdate();
        }
      });
      observeMutationTarget(structureObserver, root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-fd-vo-mapping-error', 'data-fd-atc', 'name', 'type']
      });
    }
    bindPurchaseButtonObservers();
    root.addEventListener('fd:purchase-transport', (event) => {
      purchaseTransportBusy = Boolean(event && event.detail && event.detail.busy);
      enforcePurchaseButtonState();
    });

    root.addEventListener('click', (event) => {
      const button = event.target && event.target.closest
        ? event.target.closest('[data-fd-p0-color-expand]')
        : null;
      if (!button || !root.contains(button)) return;
      const wrapper = button.closest('.fd-bcpo-color-option, [data-fd-template-color-field]');
      if (!wrapper) return;
      wrapper.dataset.fdP0ColorsExpanded = wrapper.dataset.fdP0ColorsExpanded === 'true' ? 'false' : 'true';
      compactColorOptions();
    }, true);
    const handleConfigurationEvent = (event) => {
      const target = event.target && event.target.closest
        ? event.target.closest('#bcpo, [data-fd-quantity], [data-fd-variant-id], .fd-pdp-qty-control')
        : null;
      if (!target || !root.contains(target)) return;
      const priceAffecting = Boolean(target.closest && target.closest('#bcpo, [data-fd-variant-id]'));
      if (priceAffecting && isCustomizable) {
        const selectionSignature = getObservedConfigurationSignature();
        const selectionActuallyChanged = selectionSignature !== lastObservedSelectionSignature;
        if (selectionActuallyChanged) {
          lastObservedSelectionSignature = selectionSignature;
          markSelectionChange();
          const variant = getCurrentVariant();
          const baseCents = variant && Number.isSafeInteger(Number(variant.price)) && Number(variant.price) >= 0
            ? Number(variant.price)
            : 0;
          const incompleteTitles = getIncompleteRequiredTitles();
          const mappingBlocked = root.dataset.fdVoMappingError === 'true';
          setPriceState(
            !variant ? 'error' : incompleteTitles.length || mappingBlocked ? 'incomplete' : 'loading',
            baseCents,
            null,
            incompleteTitles
          );
          scheduleUpdate(true);
        }
        if (target.closest && target.closest('#bcpo')) schedulePolicyCleanup();
        return;
      }
      scheduleUpdate(false);
      if (target.closest && target.closest('#bcpo')) schedulePolicyCleanup();
    };
    root.addEventListener('click', handleConfigurationEvent, true);
    root.addEventListener('change', handleConfigurationEvent, true);
    root.addEventListener('input', handleConfigurationEvent, true);
    root.addEventListener('fd:variant-updated', () => {
      markSelectionChange();
      lastAddOnSignature = '';
      stableAddOnReads = 0;
      if (confirmationFrame) {
        window.clearTimeout(confirmationFrame);
        confirmationFrame = 0;
      }
      updateBasePrice();
      updateSavings();
      updateConfiguredPrice();
      scheduleUpdate();
    });
    if (form) {
      window.addEventListener('submit', (event) => {
        if (event.target !== form) return;
        updateConfiguredPrice();
        if (isCustomizable && priceState === 'ready' && latestSnapshot) {
          syncPrivateProperties(latestSnapshot);
        }
      }, true);
    }
    updateBasePrice();
    updateSavings();
    updateConfiguredPrice();
    runPolicyCleanup();
    bindBcpoObserver();

    const maintenanceBackoff = [180, 500, 1200, 2400];
    let maintenanceAttempt = 0;
    const retryMissingBcpo = () => {
      if (bindBcpoObserver()) return;
      if (maintenanceAttempt >= maintenanceBackoff.length) {
        if (isCustomizable) {
          bcpoMissingFinal = true;
          scheduleUpdate();
        }
        return;
      }
      const delay = maintenanceBackoff[maintenanceAttempt];
      maintenanceAttempt += 1;
      window.setTimeout(retryMissingBcpo, delay);
    };
    retryMissingBcpo();

    const chatBackoff = [250, 500, 1000, 2000, 3500];
    let chatAttempt = 0;
    const retryChatPolish = () => {
      if (installMobileChatPolish() || chatAttempt >= chatBackoff.length) return;
      const delay = chatBackoff[chatAttempt];
      chatAttempt += 1;
      window.setTimeout(retryChatPolish, delay);
    };
    retryChatPolish();
  };

  const run = () => {
    document.querySelectorAll(
      '.fd-pdp--p0-20260710[data-fd-product], .fd-pdp--neutral-v2[data-fd-product]'
    ).forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();

/* fd-bedding-color-cards-parity-20260729 */
(function () {
  const targetHandle = 'double-layer-gauze-muslin-cotton-duvet-cover';
  const root = document.querySelector(
    `.fd-pdp.fd-pdp--p0-20260710[data-product-handle="${targetHandle}"]`
  );
  if (!root || root.dataset.fdBeddingColorCardsReady === 'true') return;
  root.dataset.fdBeddingColorCardsReady = 'true';

  const expectedColors = [
    {
      value: 'Green',
      variantId: '48131251372282',
      fileName: 'double-layer-gauze-muslin-cotton-duvet-cover-green.webp'
    },
    {
      value: 'White',
      variantId: '48131251405050',
      fileName: 'double-layer-gauze-muslin-cotton-duvet-cover-white.webp'
    },
    {
      value: 'Coffee',
      variantId: '48131251437818',
      fileName: 'double-layer-gauze-muslin-cotton-duvet-cover-coffee.webp'
    },
    {
      value: 'Light Gray',
      variantId: '48131251470586',
      fileName: 'double-layer-gauze-muslin-cotton-duvet-cover-light-gray.webp'
    },
    {
      value: 'Gray',
      variantId: '48131251503354',
      fileName: 'double-layer-gauze-muslin-cotton-duvet-cover-gray.webp'
    }
  ];
  const expectedValues = expectedColors.map((item) => item.value);
  const imagePromises = new Map();
  let mountPromise = null;
  let mountFrame = 0;

  const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const getFileName = (src) => {
    try {
      return decodeURIComponent(new URL(String(src || ''), window.location.href).pathname.split('/').pop() || '');
    } catch (error) {
      return String(src || '').split('?')[0].split('/').pop() || '';
    }
  };

  const getThumbnailUrl = (src) => {
    try {
      const url = new URL(String(src || ''), window.location.href);
      url.searchParams.set('width', '220');
      url.searchParams.delete('height');
      url.searchParams.delete('crop');
      return url.toString();
    } catch (error) {
      return String(src || '');
    }
  };

  const preloadImage = (src) => {
    if (imagePromises.has(src)) return imagePromises.get(src);
    const promise = new Promise((resolve, reject) => {
      const image = new Image();
      const finish = () => {
        if (image.naturalWidth > 0 && image.naturalHeight > 0) resolve(src);
        else reject(new Error(`Color image failed to load: ${src}`));
      };
      image.onload = finish;
      image.onerror = () => reject(new Error(`Color image failed to load: ${src}`));
      image.src = src;
      if (image.complete) finish();
    });
    imagePromises.set(src, promise);
    return promise;
  };

  const getSourceItems = () => {
    const sourceField = root.querySelector(
      '[data-fd-theme-color-option][data-fd-option-position="1"]'
    );
    if (!sourceField) return null;
    const buttons = Array.from(sourceField.querySelectorAll('[data-fd-option-value]'));
    if (buttons.length !== expectedColors.length) return null;

    const items = [];
    for (const expected of expectedColors) {
      const matches = buttons.filter(
        (button) => normalize(button.dataset.optionValue) === expected.value
      );
      if (matches.length !== 1) return null;
      const button = matches[0];
      const image = normalize(button.dataset.image);
      if (
        String(button.dataset.variantId || '') !== expected.variantId
        || getFileName(image) !== expected.fileName
      ) {
        return null;
      }
      items.push({
        value: expected.value,
        variantId: expected.variantId,
        image,
        thumbnail: getThumbnailUrl(image)
      });
    }
    return items;
  };

  const getNativeColorControl = () => {
    const select = root.querySelector(
      '#bcpo .fd-bcpo-color-option select[data-option="option1"],'
      + ' #bcpo select#bcpo-select-option-0[data-option="option1"]'
    );
    if (!select) return null;
    const wrapper = select.closest('.selector-wrapper');
    if (!wrapper) return null;
    const title = normalize(
      wrapper.querySelector('.bcpo-title')?.textContent
      || select.getAttribute('aria-label')
    ).replace(/:$/, '');
    const values = Array.from(select.options)
      .map((option) => normalize(option.value))
      .filter(Boolean);
    if (
      title.toLowerCase() !== 'color'
      || values.length !== expectedValues.length
      || values.some((value, index) => value !== expectedValues[index])
    ) {
      return null;
    }
    return { select, wrapper };
  };

  const getTrack = () => root.querySelector(
    '#bcpo .fd-bedding-color-card-track[data-fd-bedding-color-card-track]'
  );

  const syncSelectedCard = (value) => {
    const track = getTrack();
    if (!track) return;
    const selectedValue = normalize(value);
    track.querySelectorAll('[data-fd-bedding-color-value]').forEach((button) => {
      const selected = normalize(button.dataset.fdBeddingColorValue) === selectedValue
        && expectedValues.includes(selectedValue);
      button.classList.toggle('fd-bedding-color-card--selected', selected);
      button.setAttribute('aria-checked', selected ? 'true' : 'false');
    });
    if (selectedValue) track.removeAttribute('data-invalid');
  };

  const setNativeValue = (select, value) => {
    const descriptor = Object.getOwnPropertyDescriptor(
      window.HTMLSelectElement.prototype,
      'value'
    );
    if (descriptor && descriptor.set) descriptor.set.call(select, value);
    else select.value = value;
    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const restoreNativeSelect = (select) => {
    if (!select) return;
    select.classList.remove('fd-bedding-color-native-select');
    const originalTabIndex = select.dataset.fdBeddingOriginalTabIndex;
    if (originalTabIndex === '__none__') select.removeAttribute('tabindex');
    else if (typeof originalTabIndex === 'string') select.setAttribute('tabindex', originalTabIndex);
    delete select.dataset.fdBeddingOriginalTabIndex;
  };

  const removeInvalidProxy = () => {
    const track = getTrack();
    if (track) track.remove();
    root.querySelectorAll('#bcpo select.fd-bedding-color-native-select').forEach(
      restoreNativeSelect
    );
  };

  const buildProxy = async () => {
    const sourceItems = getSourceItems();
    const nativeControl = getNativeColorControl();
    if (!sourceItems || !nativeControl) {
      removeInvalidProxy();
      return false;
    }

    const signature = sourceItems
      .map((item) => `${item.value}|${item.variantId}|${getFileName(item.image)}`)
      .join('||');
    const existing = getTrack();
    if (
      existing
      && existing.dataset.fdBeddingColorSignature === signature
      && nativeControl.select.classList.contains('fd-bedding-color-native-select')
    ) {
      syncSelectedCard(nativeControl.select.value);
      return true;
    }

    try {
      await Promise.all(sourceItems.map((item) => preloadImage(item.thumbnail)));
    } catch (error) {
      removeInvalidProxy();
      return false;
    }

    const currentItems = getSourceItems();
    const currentControl = getNativeColorControl();
    if (!currentItems || !currentControl) {
      removeInvalidProxy();
      return false;
    }
    const currentSignature = currentItems
      .map((item) => `${item.value}|${item.variantId}|${getFileName(item.image)}`)
      .join('||');
    if (currentSignature !== signature) {
      removeInvalidProxy();
      return false;
    }

    if (existing) existing.remove();
    const track = document.createElement('div');
    track.className = 'fd-bedding-color-card-track';
    track.setAttribute('data-fd-bedding-color-card-track', '');
    track.dataset.fdBeddingColorSignature = signature;
    track.setAttribute('role', 'radiogroup');
    track.setAttribute('aria-label', 'Color');

    currentItems.forEach((item) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'fd-bedding-color-card';
      button.dataset.fdBeddingColorValue = item.value;
      button.dataset.fdBeddingVariantId = item.variantId;
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', 'false');
      button.setAttribute('aria-label', item.value);

      const image = document.createElement('span');
      image.className = 'fd-bedding-color-card__image';
      image.setAttribute('aria-hidden', 'true');
      image.style.backgroundImage = `url("${item.thumbnail.replace(/"/g, '\\"')}")`;

      const label = document.createElement('span');
      label.className = 'fd-bedding-color-card__label';
      label.textContent = item.value;

      button.append(image, label);
      track.appendChild(button);
    });

    track.addEventListener('click', (event) => {
      const button = event.target.closest('[data-fd-bedding-color-value]');
      if (!button || !track.contains(button)) return;
      const activeControl = getNativeColorControl();
      const value = normalize(button.dataset.fdBeddingColorValue);
      if (!activeControl || !expectedValues.includes(value)) return;
      syncSelectedCard(value);
      setNativeValue(activeControl.select, value);
    });

    currentControl.select.insertAdjacentElement('beforebegin', track);
    if (!Object.prototype.hasOwnProperty.call(
      currentControl.select.dataset,
      'fdBeddingOriginalTabIndex'
    )) {
      currentControl.select.dataset.fdBeddingOriginalTabIndex =
        currentControl.select.hasAttribute('tabindex')
          ? currentControl.select.getAttribute('tabindex')
          : '__none__';
    }
    currentControl.select.classList.add('fd-bedding-color-native-select');
    currentControl.select.setAttribute('tabindex', '-1');
    syncSelectedCard(currentControl.select.value);
    return true;
  };

  const mount = () => {
    if (mountPromise) return mountPromise;
    mountPromise = buildProxy().finally(() => {
      mountPromise = null;
    });
    return mountPromise;
  };

  const scheduleMount = () => {
    if (mountFrame) return;
    const run = () => {
      mountFrame = 0;
      mount();
    };
    if (window.requestAnimationFrame) mountFrame = window.requestAnimationFrame(run);
    else run();
  };

  root.addEventListener('change', (event) => {
    const nativeControl = getNativeColorControl();
    if (nativeControl && event.target === nativeControl.select) {
      syncSelectedCard(nativeControl.select.value);
    }
  }, true);

  root.addEventListener('invalid', (event) => {
    const nativeControl = getNativeColorControl();
    if (!nativeControl || event.target !== nativeControl.select) return;
    const track = getTrack();
    if (!track) return;
    track.setAttribute('data-invalid', 'true');
    const firstCard = track.querySelector('[data-fd-bedding-color-value]');
    if (firstCard) window.setTimeout(() => firstCard.focus({ preventScroll: true }), 0);
  }, true);

  if (window.MutationObserver) {
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.type === 'childList')) scheduleMount();
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  mount();
  window.setTimeout(scheduleMount, 600);
  window.setTimeout(scheduleMount, 1800);
  window.setTimeout(scheduleMount, 3200);
})();


/* FD_J4018_LAYER_NOTE_20260810_V2_START
 * Show a short material note only while the exact J401-8 color is selected.
 */
(function () {
  "use strict";

  var ROOT_SELECTOR = "[data-fd-product]";
  var WRAPPER_SELECTOR = [
    "#bcpo .fd-bcpo-color-option",
    "[data-fd-template-color-field]",
    "[data-fd-theme-color-option]"
  ].join(",");
  var CHOICE_SELECTOR = [
    ".fd-bcpo-color-choice",
    "[data-fd-template-bcpo-color]",
    "[data-fd-option-value]",
    "input",
    "option"
  ].join(",");
  var NOTE_SELECTOR = "[data-fd-j4018-layer-note]";
  var TARGET_PATTERN = /(?:^|[^A-Z0-9])#?J401-8(?![A-Z0-9-])/i;

  function nodeValue(node) {
    if (!node) return "";
    return [
      node.getAttribute && node.getAttribute("data-label"),
      node.getAttribute && node.getAttribute("data-value"),
      node.getAttribute && node.getAttribute("data-option-value"),
      node.getAttribute && node.getAttribute("data-fd-color-name"),
      node.getAttribute && node.getAttribute("data-fd-current-color-value"),
      node.getAttribute && node.getAttribute("value"),
      node.getAttribute && node.getAttribute("aria-label"),
      node.getAttribute && node.getAttribute("title"),
      node.textContent
    ].filter(Boolean).join(" ");
  }

  function isTarget(node) {
    return TARGET_PATTERN.test(nodeValue(node));
  }

  function isSelected(node) {
    if (!node) return false;
    if (node.matches && node.matches("input:checked, option:checked")) return true;
    if (node.getAttribute) {
      if (node.getAttribute("aria-pressed") === "true") return true;
      if (node.getAttribute("aria-checked") === "true") return true;
    }
    if (
      node.classList &&
      (node.classList.contains("fd-bcpo-is-selected") ||
        node.classList.contains("is-selected"))
    ) {
      return true;
    }
    if (node.querySelector && node.querySelector("input:checked, option:checked")) {
      return true;
    }
    return false;
  }

  function choicesFor(wrapper) {
    return Array.prototype.slice.call(wrapper.querySelectorAll(CHOICE_SELECTOR));
  }

  function directNote(wrapper) {
    for (var i = 0; i < wrapper.children.length; i += 1) {
      if (wrapper.children[i].matches(NOTE_SELECTOR)) return wrapper.children[i];
    }
    return null;
  }

  function makeNote() {
    var note = document.createElement("p");
    note.setAttribute("data-fd-j4018-layer-note", "");
    note.setAttribute("role", "status");
    note.setAttribute("aria-live", "polite");
    note.textContent = "Rosy When Layered";
    note.hidden = true;
    note.style.cssText = [
      "box-sizing:border-box",
      "display:none",
      "width:100%",
      "grid-column:1 / -1",
      "flex-basis:100%",
      "margin:8px 0 0",
      "padding:0",
      "color:#7b6253",
      "font-size:12px",
      "font-weight:500",
      "line-height:1.4"
    ].join(";");
    return note;
  }

  function syncWrapper(wrapper) {
    var choices = choicesFor(wrapper);
    var targets = choices.filter(isTarget);
    var note = directNote(wrapper);

    if (!targets.length) {
      if (note) note.remove();
      return;
    }

    if (!note) note = makeNote();
    if (wrapper.lastElementChild !== note) wrapper.appendChild(note);

    var show = targets.some(isSelected);
    note.hidden = !show;
    note.style.display = show ? "block" : "none";
  }

  function init(root) {
    if (!root || root.getAttribute("data-fd-j4018-note-ready") === "true") return;
    root.setAttribute("data-fd-j4018-note-ready", "true");

    var queued = false;
    function sync() {
      queued = false;
      var wrappers = root.querySelectorAll(WRAPPER_SELECTOR);
      for (var i = 0; i < wrappers.length; i += 1) syncWrapper(wrappers[i]);
    }
    function schedule() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(sync);
    }

    root.addEventListener("click", schedule, true);
    root.addEventListener("change", schedule, true);
    root.addEventListener("input", schedule, true);
    root.addEventListener("fd:variant-updated", schedule);

    var observer = new MutationObserver(schedule);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "aria-pressed", "aria-checked", "checked", "value"]
    });

    sync();
    window.setTimeout(schedule, 300);
    window.setTimeout(schedule, 900);
    window.setTimeout(schedule, 1800);
  }

  function boot(scope) {
    var base = scope && scope.querySelectorAll ? scope : document;
    if (base.matches && base.matches(ROOT_SELECTOR)) init(base);
    var roots = base.querySelectorAll(ROOT_SELECTOR);
    for (var i = 0; i < roots.length; i += 1) init(roots[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { boot(document); }, { once: true });
  } else {
    boot(document);
  }
  document.addEventListener("shopify:section:load", function (event) {
    boot(event.target);
  });
})();
/* FD_J4018_LAYER_NOTE_20260810_V2_END */
